import React, { createContext, useContext, useEffect, useState } from 'react';
import api, { setToken, getToken } from '../services/api';
import { setObservabilityUser, clearObservabilityUser } from '../lib/observability';
import { hasEffectivePermission } from '../lib/permissions';

const AuthContext = createContext(null);

// v3.7.0 — módulos default (todos ligados). Frontend usa fallback se o tenant
// não carregar por algum motivo, evitando esconder tudo.
const ALL_MODULES_ON = {
  pdv: true, mesas: true, comandas: true, delivery: true,
  kds: true, estoque: true, financeiro: true, relatorios: true,
};

export const AuthProvider = ({ children }) => {
  const [user, setUser] = useState(null);
  const [tenant, setTenant] = useState(null);
  const [subscription, setSubscription] = useState(null);
  const [loading, setLoading] = useState(true);
  const [suspended, setSuspended] = useState(false);

  const fetchMe = async () => {
    try {
      const { data } = await api.get('/auth/me-full');
      setUser(data.user);
      setTenant(data.tenant);
      setSubscription(data.tenant?.subscription || null);
      setSuspended(false);
      setObservabilityUser({ id: data.user?.id, email: data.user?.email, tenant_id: data.user?.tenant_id, role: data.user?.role });
    } catch (err) {
      if (err.response?.status === 403) {
        setSuspended(true);
        setToken(null);
      } else {
        setUser(null);
        setTenant(null);
        setToken(null);
      }
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    if (getToken()) fetchMe();
    else setLoading(false);
  }, []);

  const login = async (usernameOrEmail, password) => {
    const { data } = await api.post('/auth/login', { username_or_email: usernameOrEmail, password });
    setToken(data.access_token);
    setUser(data.user);
    setTenant(data.tenant);
    setSubscription(data.tenant?.subscription || null);
    setObservabilityUser({ id: data.user?.id, email: data.user?.email, tenant_id: data.user?.tenant_id, role: data.user?.role });
    return data.user;
  };

  const logout = () => {
    setToken(null);
    setUser(null);
    setTenant(null);
    setSubscription(null);
    clearObservabilityUser();
    window.location.href = '/admin/login';
  };

  const hasRole = (...roles) => !!user && roles.includes(user.role);
  const hasPermission = (permission, fallbackRoles) => hasEffectivePermission(user, permission, fallbackRoles);

  // v3.7.0 — checa se um módulo está ativado no tenant atual.
  // SuperAdmin sempre tem acesso a tudo.
  const hasModule = (moduleKey) => {
    if (!user) return false;
    if (user.role === 'superadmin') return true;
    const modules = tenant?.modules_enabled || ALL_MODULES_ON;
    return modules[moduleKey] !== false;
  };

  return (
    <AuthContext.Provider value={{
      user, tenant, subscription, loading, suspended, login, logout, hasRole, hasPermission, hasModule,
      refresh: fetchMe,
    }}>
      {children}
    </AuthContext.Provider>
  );
};

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error('useAuth must be used within AuthProvider');
  return ctx;
};
